// Campaign + Template send-side shapes, moved into bt-common so the
// CampaignLog snapshot (campaign-log.ts) can type its `campaign`
// field strictly rather than as `z.unknown()` (#53). Mongoose
// schemas (campaignSchema, templateSchema) stay backend-side —
// Mongoose isn't a bt-common dependency. This file exposes the
// zod-validated shape consumers read.

import { z } from 'zod';

// ── Template ─────────────────────────────────────────────────────────

/** Send-asset descriptor on a template. Mirrors the v1-side
 *  MessageAssetSchema (bt/user-message.ts) — same fields, different
 *  consumer (campaign template vs. send-queue). */
export const TemplateAssetSchema = z.object({
  name: z.string().nullable().optional(),
  type: z.string().nullable().optional(),
  url: z.string(),
});
export type TemplateAsset = z.infer<typeof TemplateAssetSchema>;

/** Template button (snippet / template UI buttons attached to a send). */
export const TemplateButtonSchema = z.object({
  /** "Website" | "Call to number" | "Quick respond" */
  buttonType: z.string().optional(),
  value: z.string().optional(),
});
export type TemplateButton = z.infer<typeof TemplateButtonSchema>;

export const TemplateSchema = z.object({
  _id: z.union([z.string(), z.number(), z.null()]).optional(),
  tempId: z.string().optional(),
  name: z.string().optional(),
  owner: z.union([z.string(), z.number(), z.null()]).optional(),
  message: z.string().optional(),
  asset: TemplateAssetSchema.optional(),
  /** "snippet" | "template" */
  templateType: z.string().optional(),
  buttons: z.array(TemplateButtonSchema).optional(),
  snippetShortcut: z.string().optional(),
  version: z.number().optional(),
  isPoll: z.boolean().optional(),
  /** Poll payload — loose because the campaign UI accepts varied shapes
   *  (`{question, options}` vs `{name, options}` legacy). The strict
   *  send-side poll shape lives on UserMessage.poll (bt/user-message.ts). */
  poll: z.unknown().optional(),
  /** Link preview attached to a text template — same shape as
   *  UserMessage.linkPreview (which lives in bt/user-message.ts under
   *  the persisted form). */
  linkPreview: z.unknown().optional(),
  sendLinkPreview: z.boolean().optional(),
  deleted: z.boolean(),
  /** Mongoose-managed timestamps when {timestamps: true} on the schema. */
  createdAt: z.union([z.date(), z.string()]).optional(),
  updatedAt: z.union([z.date(), z.string()]).optional(),
});
export type Template = z.infer<typeof TemplateSchema>;

// ── Campaign nested types ────────────────────────────────────────────

export const RecurrenceUnitSchema = z.enum([
  'minute', 'hour', 'day', 'week', 'month', 'year',
]);
export type RecurrenceUnit = z.infer<typeof RecurrenceUnitSchema>;

export const ScheduleSchema = z.object({
  dueDate: z.union([z.date(), z.string()]).optional(),
  isTimeValid: z.boolean().optional(),
  isRecurring: z.boolean().optional(),
  recurrenceQuantity: z.string().optional(),
  recurenceUnit: RecurrenceUnitSchema.optional(),
  cancelIfReceived: z.boolean().optional(),
  rruleset: z.string().optional(),
  calendarMessage: z.boolean().optional(),
});
export type Schedule = z.infer<typeof ScheduleSchema>;

// NOTE (2026-06-27): Of these timing fields, only `messageInterval` actually
// affects sending — it is the per-message pacing delay (default 8s; see the
// hardcoded default in whatsapp-scheduler-backend campaigns.service.ts). The
// "advanced" timing options — the sending-hours window (`enforceSendingWindow`
// + `startTime`/`endTime`) and the send-a-batch-then-rest pacing (`sendInterval`)
// — are NOT IMPLEMENTED on the send/dispatch path (nothing reads them) AND their
// UI (`CampaignTimingAdvancedOptions`) is HIDDEN, commented out in
// whatsapp-scheduler bulk-set-time-and-date.tsx. They persist as fields/defaults
// but have no runtime effect today — do not assume a campaign honors them.
// `automaticTiming` only feeds the messageInterval calculation inside that
// (hidden) advanced UI; it is not consulted at send time either.
// #412 — OPTIONAL randomized gap between campaign messages. Absent = OFF = the default fixed
// CAMPAIGN_FIXED_GAP_SECONDS spacing, exactly as before. When present, each gap between two
// consecutive recipients is drawn uniformly (whole seconds) from [minSeconds, maxSeconds], once,
// when the send times are precomputed. `messageInterval` stays untouched (it is documented
// above but not read by the send path; the fixed spacing is a backend/FE constant).
export const CAMPAIGN_FIXED_GAP_SECONDS = 7;
export const CAMPAIGN_RANDOM_GAP_MIN_SECONDS = 5;
export const CAMPAIGN_RANDOM_GAP_MAX_SECONDS = 120;

export const CampaignRandomGapSchema = z
  .object({
    minSeconds: z.number().int().min(CAMPAIGN_RANDOM_GAP_MIN_SECONDS).max(CAMPAIGN_RANDOM_GAP_MAX_SECONDS),
    maxSeconds: z.number().int().min(CAMPAIGN_RANDOM_GAP_MIN_SECONDS).max(CAMPAIGN_RANDOM_GAP_MAX_SECONDS),
  })
  .refine((g) => g.minSeconds <= g.maxSeconds, {
    message: 'minSeconds must be <= maxSeconds',
    path: ['minSeconds'],
  });
export type CampaignRandomGap = z.infer<typeof CampaignRandomGapSchema>;

/** Returns a human-readable error for an invalid random-gap setting, or null when valid. */
export function campaignRandomGapError(gap: unknown): string | null {
  const r = CampaignRandomGapSchema.safeParse(gap);
  if (r.success) return null;
  return r.error.issues.map((i) => `${i.path.join('.') || 'randomGap'}: ${i.message}`).join('; ');
}

export const CampaignTimingSchema = z.object({
  messageInterval: z.number(),
  randomGap: CampaignRandomGapSchema.optional(), // #412 — opt-in jittered gap (absent = fixed 7s) // IMPLEMENTED — per-message send delay (default 8s)
  // IMPLEMENTED (2026-07) — opt-in absolute staleness window: a campaign message that cannot be
  // sent within this many minutes of ITS OWN planned send time (engine offline → backlog) is
  // EXPIRED (skipped + logged status:'expired') instead of sent hours late. Enforced server-side
  // at generation time (campaign-actions claim loop) and at send time (acquire-send-slot dueDate
  // gate). undefined/0 = off (default). Measured per-message, NOT from campaign start, so large
  // campaigns pacing normally are never truncated.
  maxSendWindowMinutes: z.number().positive().optional(),
  enforceSendingWindow: z.boolean().optional(), // NOT IMPLEMENTED — sending-window (hidden UI)
  startTime: z.object({ // NOT IMPLEMENTED — part of the hidden sending-window
    hour: z.number(),
    minute: z.number(),
    offset: z.number().optional(),
  }),
  endTime: z.object({ // NOT IMPLEMENTED — part of the hidden sending-window
    hour: z.number(),
    minute: z.number(),
    offset: z.number().optional(),
  }),
  sendInterval: z.object({ // NOT IMPLEMENTED — send/rest batching (hidden UI)
    send: z.number(),
    rest: z.number(),
  }).optional(),
  automaticTiming: z.object({ // only feeds messageInterval calc in the hidden advanced UI
    active: z.boolean(),
    age: z.number(),
    activity: z.number(),
  }),
});
export type CampaignTiming = z.infer<typeof CampaignTimingSchema>;

// ── Campaign ─────────────────────────────────────────────────────────

export const CampaignStatusList = [
  'pending', 'active', 'running', 'paused', 'completed', 'aborted',
  // `complete-send` kept for backwards-compat only; new writes use
  // `complete-sent` / `complete-delivered`.
  'complete-send', 'complete-sent', 'complete-delivered',
  // BE#154 — a completed campaign that dropped one or more audience contacts with
  // no resolvable WhatsApp ID. A "sending complete" terminal state like
  // complete-sent (drips advance, delivery-promotion still applies), but NOT a
  // clean full-send — the UI surfaces the skippedCount instead of implying full
  // delivery. Recognizers treat it via isSendingCompleteStatus().
  'complete-with-skips',
] as const;
export const CampaignStatusSchema = z.enum(CampaignStatusList);
export type CampaignStatus = z.infer<typeof CampaignStatusSchema>;

/**
 * BE#154 — "sending is complete" terminal states: every message that COULD be
 * generated has been dispatched. Both `complete-sent` (clean) and
 * `complete-with-skips` (some no-WhatsApp-ID recipients dropped) qualify, so
 * drip advancement and the complete-delivered promotion must treat them alike.
 * Central so a new send-complete variant is recognized everywhere at once.
 */
export const SENDING_COMPLETE_STATUSES: readonly CampaignStatus[] = ['complete-sent', 'complete-with-skips'];
export function isSendingCompleteStatus(status: string | null | undefined): boolean {
  return status === 'complete-sent' || status === 'complete-with-skips';
}

export const CampaignSchema = z.object({
  _id: z.union([z.string(), z.number(), z.null()]).optional(),
  name: z.string().optional(),
  /** Audience-target board ids (#71 — single canonical field; the
   *  legacy `audienceId` / `boardId` aliases were dropped from the
   *  v1 mapper). */
  boardIDArray: z.array(z.union([z.string(), z.number()])).optional(),
  isRecurring: z.boolean().optional(),
  template: TemplateSchema.optional(),
  campaignStatus: CampaignStatusSchema.optional(),
  owner: z.union([z.string(), z.number(), z.null()]).optional(),
  schedule: ScheduleSchema.optional(),
  timing: CampaignTimingSchema.optional(),
  testSessionId: z.string().optional(),
  contactCount: z.number().optional(),
  // BE#154 — dispatch accounting for audience contacts that could not be
  // addressed. `skippedCount` = recipients dropped for having NO resolvable
  // WhatsApp ID (silent before). `intendedRecipientCount` = the audience size
  // before dedup/skip, so the UI can show "N of M messaged, K skipped".
  skippedCount: z.number().optional(),
  intendedRecipientCount: z.number().optional(),
  /** Campaign-level statistics blob — loose, written by various
   *  dashboard paths. */
  campaignStatistics: z.unknown().optional(),
  statisticsUpdatedAt: z.string().optional(),
  startFailureReason: z.string().optional(),
  clientId: z.string().optional(),
  clientType: z.enum(['gateway', 'whatsapp', 'frontend']).optional(),
  /** Lifecycle timestamps surfaced by the v1 API (B9). */
  startedAt: z.union([z.date(), z.string()]).optional(),
  completedAt: z.union([z.date(), z.string()]).optional(),
  abortedAt: z.union([z.date(), z.string()]).optional(),
  /** Origin tag: `true` when the campaign was created via the public v1 API
   *  (vs the dashboard/extension). Attribution only — mirrors the
   *  user-messages queue's `api` flag (bt/user-message.ts). */
  api: z.boolean().optional(),
  /** Drip linkage — set only when this campaign is one step of a drip.
   *  Normal campaigns leave both absent; the campaigns listing filters
   *  `dripId` null/absent so drip-step campaigns don't surface there. */
  dripId: z.union([z.string(), z.null()]).optional(),
  dripStepIndex: z.number().int().nonnegative().optional(),
  /** ORCH#102 recurring-campaign linkage — set on a campaign spawned as the NEXT
   *  occurrence of a recurring series (recurring-campaign-orchestrator.ts), pointing
   *  at the occurrence that completed. The root occurrence leaves it unset. Backed by
   *  a partial-unique index (campaigns.model.ts) that makes the relaunch idempotent. */
  parentCampaignId: z.string().optional(),
  /** Optional recipient exclusion, by whatsappId (same key the dispatch
   *  dedups on). Normal campaigns omit it → behaviour is unchanged; a drip
   *  step passes the parent drip's accumulated exit set so already-exited
   *  recipients are skipped when the step's audience is resolved. */
  excludeWhatsappIds: z.array(z.string()).optional(),
  /** #922 S0b — the sender (`wa_senders._id`) this campaign sends from. Server-owned, resolved once at
   *  create; every per-recipient message is minted with it. Absent = the owner's default sender. */
  senderId: z.string().optional(),
  /** Mongoose-managed when {timestamps: true}. */
  createdAt: z.union([z.date(), z.string()]).optional(),
  updatedAt: z.union([z.date(), z.string()]).optional(),
  deleted: z.boolean().optional(),
});
export type Campaign = z.infer<typeof CampaignSchema>;
